import { useEffect } from "react";
import { onFeatureUsage } from "@/utils/gtm";
import useTabStore from "@/stores/useTabStore";
import { useOnboardingStore } from "../useOnboardingStore";
import { matches } from "./triggers";
import { RAIL_STEPS } from "./steps";
import { gainedSelection } from "./railTargets";

/**
 * Advances the rail by watching the product's existing instrumentation.
 *
 * Same approach as the demo guide's useStepDetection and the checklist's
 * useChecklistDetection, and for the same reason: the alternative is threading
 * "tell the guide I happened" callbacks into every surface the rail might point
 * at. Subscribing to onFeatureUsage instead means the rail observes the product
 * without the product knowing it exists — and the signals are already emitted
 * for GA4, so a step that can be measured is a step that can be detected.
 *
 * This is a deliberate COPY of the demo guide's hook rather than a shared
 * abstraction. The three differences are each load-bearing and two of them are
 * *inversions*, not parameters:
 *
 *   1. The tabsLoaded guard is BACK. useStepDetection drops it on purpose,
 *      because the flag only goes true after an authenticated layout read and
 *      would disable the whole subscription for a logged-out visitor. Here the
 *      user is authenticated and the pre-load default layout must not count.
 *   2. The demo matches one hardcoded DEMO_WATCHLIST_ID; a subscriber has no
 *      such id, so the test is "a selection was added on the right widget".
 *   3. Different store, different live-run predicate, different advance action.
 *
 * A shared hook taking three injected strategies would be harder to read than
 * the duplication.
 */
export function useRailDetection(enabled: boolean) {
  const advanceRailStep = useOnboardingStore((s) => s.advanceRailStep);

  useEffect(() => {
    if (!enabled) return;

    /**
     * Resolve the live step at fire time — never close over stale state.
     *
     * This is what keeps the effect deps stable, so the subscriptions are set
     * up once and never torn down on a step change. A version that closed over
     * the current step would re-subscribe on every advance and drop events
     * mid-flight.
     */
    const currentStep = () => {
      const rail = useOnboardingStore.getState().state?.rail;
      if (!rail || rail.completedAt || rail.exitedAt) return null;
      const stepId = rail.stepIds[rail.step];
      return RAIL_STEPS.find((s) => s.id === stepId) ?? null;
    };

    const unsubscribeFeatureUsage = onFeatureUsage((event) => {
      const step = currentStep();
      if (step && matches(step.trigger, event)) advanceRailStep();
    });

    const unsubscribeTabStore = useTabStore.subscribe((state, prevState) => {
      // Unlike the demo guide, this guard stays: it exists to avoid acting on
      // the default layout the store holds before the server read lands.
      if (!state.tabsLoaded || !prevState.tabsLoaded) return;

      const step = currentStep();
      if (!step || step.trigger.kind !== "widgetFilters") return;

      const baseId = step.target?.baseId;
      if (!baseId) return;

      /**
       * Matched against any widget of the right TYPE on ANY tab — not against
       * the grid id currently being ringed. Someone who applies the watchlist
       * on a different tab has still done the thing, and this does not dead-end
       * when the ring resolves to null.
       */
      const applied = state.tabs.some((tab) => {
        const prevTab = prevState.tabs.find((t) => t.id === tab.id);
        if (!prevTab) return false;
        return Object.entries(tab.widgetFilters ?? {}).some(
          ([widgetId, data]) =>
            (widgetId === baseId || widgetId.startsWith(`${baseId}-`)) &&
            gainedSelection(data, prevTab.widgetFilters?.[widgetId])
        );
      });
      if (applied) advanceRailStep();
    });

    return () => {
      unsubscribeFeatureUsage();
      unsubscribeTabStore();
    };
  }, [enabled, advanceRailStep]);
}
